import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { articles as staticArticles } from '../data/siteData';
import { blogImages } from '../assets';
import { getPublicArticles } from '../services/contentApi';
import useCmsPage from '../hooks/useCmsPage';
import useSeoMeta from '../hooks/useSeoMeta';
import './Actualites.css';

const ALL_CATS = ['Tous', 'Intelligence Artificielle', 'Cybersécurité', 'Tech & Industrie', 'Mobilité Électrique', 'IA & Innovation'];

export default function Actualites() {
  const { sections, page } = useCmsPage('actualites');
  useSeoMeta(page, { title: 'Actualités — Tech Talent Accelerator', desc: 'Suivez les dernières actualités tech, IA et innovation de TTA.' });
  const getSec = type => sections.find(s => s.type === type)?.content || {};
  const cmsHero = getSec('hero');

  const [filter, setFilter] = useState('Tous');
  const [articles, setArticles] = useState(staticArticles);

  useEffect(() => {
    getPublicArticles().then(data => {
      if (!data || !data.length) return;
      // Map API fields to siteData format, merge tags/readTime/image from static by slug
      const mapped = data.map(a => {
        const stat = staticArticles.find(s => s.slug === a.slug) || {};
        const pub = a.published_at || a.created_at || '';
        const dateObj = pub ? new Date(pub) : null;
        const dateLabel = dateObj
          ? dateObj.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })
          : stat.dateLabel || '';
        return {
          ...stat,
          id: a.id,
          slug: a.slug,
          title: a.titre || stat.title,
          excerpt: a.resume || stat.excerpt,
          category: a.categorie || stat.category,
          author: a.auteur || stat.author,
          date: pub.slice(0, 10) || stat.date,
          dateLabel,
          imageSrc: a.image_url || null,
          image: stat.image || 'blog-001',
          readTime: stat.readTime || '5 min',
          tags: (Array.isArray(a.tags) && a.tags.length ? a.tags : null) || stat.tags || [],
        };
      });
      setArticles(mapped);
    });
  }, []);

  const filtered = filter === 'Tous' ? articles : articles.filter(a => a.category === filter);
  const [featured, ...rest] = filtered;

  return (
    <main className="actualites-page">

      <section className="actualites-hero">
        <div className="actualites-hero__bg" />
        <div className="page-hero-right__orb" data-parallax="0.55" />
        <div className="page-hero-right__orb2" data-parallax="-0.20" />
        <div className="page-hero-layout">
          <div className="page-hero-left">
            <div className="section-eyebrow">{cmsHero.eyebrow || 'Actualités & Blog'}</div>
            <h1 className="section-title" style={{ fontSize: '44px', maxWidth: '520px' }}>
              {cmsHero.title || "La tech, l'IA &"}<br /><span className="grad-text">{cmsHero.title_grad || 'le numérique africain'}</span>
            </h1>
            <p className="section-subtitle" style={{ maxWidth: '460px' }}>
              {cmsHero.subtitle || "Analyses, tendances et insights sur les technologies qui transforment le continent. Par l'équipe TTA."}
            </p>
            <div className="hero-filter-bar">
              {ALL_CATS.map(c => (
                <button
                  key={c}
                  className={`hero-filter-btn ${filter === c ? 'active' : ''}`}
                  onClick={() => setFilter(c)}
                >
                  {c}
                </button>
              ))}
            </div>
          </div>
          <div className="page-hero-right">
            <div className="phm-grid">
              {[
                { num: '6', label: 'Articles publiés ce mois', bar: '60%', delay: '0s', barDelay: '0.2s',
                  icon: <><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></> },
                { num: '5', label: 'Catégories tech couvertes', bar: '50%', delay: '1.5s', barDelay: '0.4s',
                  icon: <><path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/><line x1="7" y1="7" x2="7.01" y2="7"/></> },
                { num: '~5mn', label: 'Temps de lecture moyen', bar: '45%', delay: '3s', barDelay: '0.6s',
                  icon: <><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></> },
                { num: '100%', label: 'Contenus rédigés par des experts', bar: '100%', delay: '4.5s', barDelay: '0.8s',
                  icon: <><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></> },
              ].map((m, i) => (
                <div key={i} className="phm-card" style={{ animationDelay: m.delay }}>
                  <div className="phm-card__icon">
                    <svg viewBox="0 0 24 24">{m.icon}</svg>
                  </div>
                  <div className="phm-card__num">{m.num}</div>
                  <div className="phm-card__label">{m.label}</div>
                  <div className="phm-card__bar">
                    <div className="phm-card__bar-fill" style={{ width: m.bar, animationDelay: m.barDelay }} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="section">

        {/* FEATURED ARTICLE */}
        {featured && (
          <Link to={`/actualites/${featured.slug}`} className="actu-featured">
            <div className="actu-featured__img-wrap">
              <img src={featured.imageSrc || blogImages[featured.image]} alt={featured.title} className="actu-featured__img" />
              <span className="actu-featured__cat">{featured.category}</span>
            </div>
            <div className="actu-featured__body">
              <div className="actu-featured__meta">
                <span>{featured.dateLabel}</span>
                <span className="actu-featured__dot" />
                <span>{featured.readTime}</span>
                <span className="actu-featured__dot" />
                <span>{featured.author}</span>
              </div>
              <h2 className="actu-featured__title">{featured.title}</h2>
              <p className="actu-featured__excerpt">{featured.excerpt}</p>
              <div className="actu-featured__tags">
                {featured.tags.map(t => <span key={t} className="actu-tag">{t}</span>)}
              </div>
              <span className="actu-featured__cta">Lire l'article →</span>
            </div>
          </Link>
        )}

        {/* GRID */}
        {rest.length > 0 && (
          <div className="actu-grid">
            {rest.map(a => (
              <Link to={`/actualites/${a.slug}`} key={a.id} className="actu-card">
                <div className="actu-card__img-wrap">
                  <img src={a.imageSrc || blogImages[a.image]} alt={a.title} className="actu-card__img" />
                  <span className="actu-card__cat">{a.category}</span>
                </div>
                <div className="actu-card__body">
                  <div className="actu-card__meta">
                    <span>{a.dateLabel}</span>
                    <span className="actu-featured__dot" />
                    <span>{a.readTime}</span>
                  </div>
                  <h3 className="actu-card__title">{a.title}</h3>
                  <p className="actu-card__excerpt">{a.excerpt}</p>
                  <div className="actu-card__footer">
                    <div className="actu-card__tags">
                      {a.tags.slice(0, 2).map(t => <span key={t} className="actu-tag">{t}</span>)}
                    </div>
                    <span className="actu-card__cta">Lire →</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}

        {filtered.length === 0 && (
          <p style={{ textAlign: 'center', color: '#888', padding: '48px 0' }}>
            Aucun article dans cette catégorie pour le moment.
          </p>
        )}
      </section>

    </main>
  );
}
